import React, { useMemo, useRef, useState } from 'react';
import { FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { FUNDING_MODAL_OVERLAY_CLASS } from './fundingDrawerUi';
import { downloadBulkImportTemplate } from '../../../utils/fundingClientIntakeUtils';
import {
  buildMappingRows,
  commitClientImport,
  mappingObjectFromRows,
  previewClientImport,
  validateClientImport,
  validationStats,
} from '../../../services/funding/fundingClientImportService';

const STEPS = ['Upload', 'Map Columns', 'Validate', 'Review', 'Import'];

const BulkImportClientsModal = ({
  open,
  onOpenChange,
  existingClients = [],
  onImportClients,
  agencyId,
  onRefreshClients,
}) => {
  const fileInputRef = useRef(null);
  const [step, setStep] = useState(0);
  const [fileName, setFileName] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [rawRows, setRawRows] = useState([]);
  const [mappingRows, setMappingRows] = useState([]);
  const [validation, setValidation] = useState(null);
  const [importedCount, setImportedCount] = useState(0);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (!open) {
      setStep(0);
      setFileName('');
      setUploadedFile(null);
      setHeaders([]);
      setRawRows([]);
      setMappingRows([]);
      setValidation(null);
      setImportedCount(0);
      setLoading(false);
    }
  }, [open]);

  const stats = useMemo(() => validationStats(validation), [validation]);
  const validatedRows = validation?.rows || [];

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setLoading(true);
    setFileName(file.name);
    setUploadedFile(file);
    try {
      const preview = await previewClientImport(file, { agencyId });
      setHeaders(preview.headers || []);
      setRawRows(preview.rows || []);
      setMappingRows(buildMappingRows(preview.headers || [], preview.detected_mapping || {}));
      setValidation(preview.validation || null);
      toast.success(`Loaded ${preview.row_count || 0} row(s) from ${file.name}`);
    } catch (error) {
      toast.error(error.message || 'Could not parse import file.');
      setFileName('');
      setUploadedFile(null);
      setHeaders([]);
      setRawRows([]);
      setMappingRows([]);
      setValidation(null);
    } finally {
      setLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const runValidation = async () => {
    if (!uploadedFile || !rawRows.length) {
      toast.error('Select a file first');
      return;
    }
    setLoading(true);
    try {
      const validationResult = await validateClientImport({
        agencyId,
        mapping: mappingObjectFromRows(mappingRows),
        rows: rawRows,
      });
      setValidation(validationResult);
      setStep(2);
    } catch (error) {
      toast.error(error.message || 'Validation failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!agencyId) {
      toast.error('Select an agency before importing clients.');
      return;
    }
    setLoading(true);
    try {
      const result = await commitClientImport({
        agencyId,
        mapping: mappingObjectFromRows(mappingRows),
        rows: rawRows,
      });
      setImportedCount(result.created || 0);
      setStep(4);
      toast.success(`${result.created || 0} clients imported`);
      onImportClients?.({ created: result.created, clientIds: result.client_ids || [] });
      onRefreshClients?.();
    } catch (error) {
      toast.error(error.message || 'Import failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        overlayClassName={FUNDING_MODAL_OVERLAY_CLASS}
        className="funding-client-modal fg-bulk-import-modal max-h-[90vh] max-w-5xl overflow-y-auto"
        data-testid="bulk-import-clients-modal"
      >
        <DialogHeader>
          <DialogTitle>Bulk Import Clients</DialogTitle>
          <DialogDescription>Upload a CSV or Excel file, map your fields, validate records, and import clients in bulk.</DialogDescription>
        </DialogHeader>

        <div className="fg-stepper" data-testid="bulk-import-stepper">
          {STEPS.map((label, index) => (
            <span key={label} className={`fg-step ${index === step ? 'active' : index < step ? 'done' : ''}`} data-testid={`bulk-step-${index}`}>
              {index + 1}. {label}
            </span>
          ))}
        </div>

        {step === 0 ? (
          <div data-testid="bulk-step-upload-panel">
            <div className="fg-upload-panel">
              <div className="fg-upload-dropzone">
                <FileSpreadsheet className="mx-auto size-10 text-[var(--fg-accent)]" />
                <strong>Drop CSV or Excel file here</strong>
                <span className="fg-upload-dropzone__hint">Supported formats: .csv, .xlsx</span>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  data-testid="bulk-import-file-input"
                  onChange={handleFile}
                  className="hidden"
                />
                <button type="button" className="fg-btn fg-btn-light mt-4" onClick={() => fileInputRef.current?.click()}>
                  Choose file
                </button>
                {fileName ? (
                  <p className="mt-3 text-sm font-semibold text-[var(--funding-text-primary)]" data-testid="bulk-import-selected-file">
                    Selected: {fileName}
                  </p>
                ) : null}
                {headers.length ? (
                  <p className="mt-2 text-sm text-[var(--funding-text-secondary)]" data-testid="bulk-import-detected-headers">
                    Detected columns: {headers.join(', ')}
                  </p>
                ) : null}
                <button type="button" className="fg-btn fg-btn-soft mt-3 w-full" onClick={downloadBulkImportTemplate}>
                  Download import template
                </button>
              </div>
            </div>
            <div className="fg-hint-box fg-info-panel">
              <strong>Required fields</strong>
              <p>First name, last name, and either email or phone.</p>
              <p className="fg-info-panel__note">SSN and EIN will be masked in preview.</p>
            </div>
          </div>
        ) : null}

        {step === 1 ? (
          <div className="fg-mapping-grid" data-testid="bulk-step-map-panel">
            {mappingRows.map((row, index) => (
              <div key={row.fieldKey} className="fg-map-row">
                <div className="fg-map-source">{row.label}</div>
                <select
                  value={row.column}
                  data-testid={`bulk-map-${row.fieldKey}`}
                  onChange={(e) => {
                    const next = [...mappingRows];
                    next[index] = { ...row, column: e.target.value };
                    setMappingRows(next);
                  }}
                >
                  <option value="">— Skip —</option>
                  {headers.map((header) => (
                    <option key={header} value={header}>{header}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        ) : null}

        {step === 2 ? (
          <div data-testid="bulk-step-validate-panel">
            <div className="fg-stats-grid">
              <div className="fg-stat-card"><span>Total rows</span><strong>{stats.total}</strong></div>
              <div className="fg-stat-card"><span>Ready</span><strong>{stats.ready}</strong></div>
              <div className="fg-stat-card"><span>Duplicates</span><strong>{stats.duplicates}</strong></div>
              <div className="fg-stat-card"><span>Errors</span><strong>{stats.errors}</strong></div>
            </div>
            <div className="fg-hint-box">Validation complete. Import valid clients and skip duplicate/error rows.</div>
          </div>
        ) : null}

        {step === 3 ? (
          <div className="overflow-x-auto rounded-2xl border border-[var(--fg-line)]" data-testid="bulk-step-review-panel">
            <table className="min-w-full text-left text-sm">
              <thead>
                <tr className="border-b border-[var(--fg-line)] bg-[var(--fg-panel-soft)] text-[var(--fg-muted)]">
                  <th className="px-3 py-2">Row</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Client name</th>
                  <th className="px-3 py-2">Email</th>
                  <th className="px-3 py-2">SSN</th>
                  <th className="px-3 py-2">EIN</th>
                  <th className="px-3 py-2">Error</th>
                </tr>
              </thead>
              <tbody>
                {validatedRows.map((row) => {
                  const mapped = row.mapped || {};
                  const clientName = [mapped.first_name, mapped.last_name].filter(Boolean).join(' ') || mapped.owner_name || '—';
                  return (
                    <tr key={row.row_number} className="border-b border-[var(--fg-line)]/70">
                      <td className="px-3 py-2">{row.row_number}</td>
                      <td className="px-3 py-2">{row.status}</td>
                      <td className="px-3 py-2">{clientName}</td>
                      <td className="px-3 py-2">{mapped.email || '—'}</td>
                      <td className="px-3 py-2 font-mono text-xs">{mapped.ssn_masked || '—'}</td>
                      <td className="px-3 py-2 font-mono text-xs">{mapped.ein_masked || '—'}</td>
                      <td className="px-3 py-2 text-rose-600">{(row.errors || []).join(' ') || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}

        {step === 4 ? (
          <div className="fg-hint-box" data-testid="bulk-step-summary-panel">
            <strong>Import complete.</strong> {importedCount} valid clients were added to the roster. Duplicate and error rows were skipped.
          </div>
        ) : null}

        <div className="fg-modal-footer">
          <button type="button" className="fg-btn fg-btn-light" onClick={() => onOpenChange(false)}>Close</button>
          {step < 4 ? (
            <>
              {step > 0 ? (
                <button type="button" className="fg-btn fg-btn-light" onClick={() => setStep((current) => current - 1)}>Back</button>
              ) : null}
              <button
                type="button"
                className="fg-btn fg-btn-primary"
                data-testid="bulk-import-next-btn"
                disabled={loading}
                onClick={() => {
                  if (step === 0) {
                    if (!fileName || !headers.length) {
                      toast.error('Select a file first');
                      return;
                    }
                    setStep(1);
                  } else if (step === 1) {
                    runValidation();
                  } else if (step === 2) {
                    setStep(3);
                  } else if (step === 3) {
                    handleImport();
                  }
                }}
              >
                {loading ? 'Working…' : step === 3 ? 'Import clients' : 'Continue'}
              </button>
            </>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BulkImportClientsModal;
